import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";

export class ShaperMemory extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
    }

    title() {
        return "Shaper Memory Utilization";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return "<h5>Shaper Memory Utilization</h5><p>RAM usage over time.</p>";
    }

    canExplain() {
        return true;
    }

    isExplainReady() {
        return this.lastGraphState !== null;
    }

    explain() {
        if (!this.lastGraphState || !this.lastGraphState.body) return null;
        const memRaw = this.lastGraphState.body.memory_percentage || [];
        const mem = memRaw.map(v => (Number.isFinite(v) ? v * 100 : null));
        if (mem.length === 0) return null;

        const maxMem = maxFinite(mem);
        if (maxMem === null) return null;

        let level = null;
        if (maxMem >= 90) {
            level = "very high";
        } else if (maxMem >= 80) {
            level = "high";
        } else {
            return null;
        }

        const threshold = level === "very high" ? 90 : 80;
        const hits = mem.filter(v => Number.isFinite(v) && v >= threshold).length;
        const total = mem.filter(v => Number.isFinite(v)).length;
        const percent = total > 0 ? Math.round((hits / total) * 100) : 0;

        let description = `Memory was ${level} at times (peak ${maxMem.toFixed(0)}%).`;
        if (total > 0 && hits > 0) {
            description += ` High samples: ${hits}/${total} (~${percent}%).`;
        }
        return { dashlet_name: this.title(), description };
    }

    buildContainer() {
        let base = super.buildContainer();
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperMemoryGraph(this.graphDivId());
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState);
        }
    }

    channelRequests() {
        return ["ShaperDetail"]; 
    }

    onMessage(msg) {
        if (msg.Data.target !== "shaper_detail") return;

        if (!this.rendered) {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }
        for (const [_key, value] of Object.entries(msg.Data.blob)) {
            this.graph.update(value);
            this.lastGraphState = value;
        }
        this.rendered = true;
    }
}

function maxFinite(values) {
    let max = null;
    values.forEach(v => {
        if (!Number.isFinite(v)) return;
        if (max === null || v > max) max = v;
    });
    return max;
}

class ShaperMemoryGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(75)
            .withTimeAxis()
            .withScaledAbsYAxisPercent("% Memory", 50)
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        this.option && this.chart.setOption(this.option);
    }

    update(data) {
        this.chart.hideLoading();

        this.option.series = [];
        this.option.xAxis.data = [];
        this.option.legend.data = [];

        const ticks = data.body.usage_ticks || [];
        // memory_percentage comes as 0.0–1.0 fraction; convert to percent
        const memRaw = data.body.memory_percentage || [];
        const mem = memRaw.map(v => (v == null ? v : v * 100));

        for (let i = 0; i < ticks.length; i++) {
            this.option.xAxis.data.push(ticks[i]);
        }

        const series = {
            type: 'line',
            data: mem,
            name: 'Memory',
            smooth: true,
            itemStyle: { color: window.graphPalette[0] },
            areaStyle: { color: window.graphPalette[0] },
        };

        this.option.series.push(series);
        this.option.legend.data.push('Memory');

        // Tooltip
        const thatData = data;
        this.option.tooltip = {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
                label: {
                    backgroundColor: 'rgba(30,30,30,0.92)',
                    color: '#fff',
                    fontFamily: 'inherit',
                    formatter: function(params) {
                        if (params.axisDimension === 'x') {
                            return new Date(parseInt(params.value, 10) * 1000).toLocaleString();
                        }
                        return params.value + '%';
                    }
                }
            },
            backgroundColor: 'rgba(30,30,30,0.92)',
            textStyle: {
                fontSize: 10,
                color: '#fff',
                fontFamily: 'inherit'
            },
            formatter: function(params) {
                let result = '<div style="font-size: 10px;">';
                if (params && params.length > 0) {
                    const ts = params[0].axisValue;
                    result += new Date(parseInt(ts, 10) * 1000).toLocaleString() + '<br/>';
                }
                const idx = params[0].dataIndex;
                const memValRaw = (thatData.body.memory_percentage || [])[idx];
                if (memValRaw !== undefined) {
                    const memVal = (memValRaw ?? 0) * 100;
                    result += '<strong>Memory:</strong> ' + memVal.toFixed(1) + '%';
                }
                result += '</div>';
                return result;
            }
        };

        // Axis pointer link
        this.option.axisPointer = {
            link: { xAxisIndex: 'all' },
            label: {
                backgroundColor: 'rgba(30,30,30,0.92)',
                color: '#fff',
                fontFamily: 'inherit'
            }
        };

        this.chart.setOption(this.option);
    }
}
